import {
  AGENT_STATUS_LABELS,
  AGENT_STATUSES,
  type AgentEnrollmentCode,
  type AgentStationSummary,
  type AgentStatus,
  type AgentSummary,
  type CreatedAgentEnrollmentCode,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { KeyRound, Plus, Server } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  ENROLLMENT_CODE_STATE_TONE,
  enrollmentCodeState,
  formatClockSkew,
  isClockSkewHigh,
} from "../../../features/agents/agent-values.js";
import {
  CreateEnrollmentCodeDialog,
  RevealEnrollmentCodeDialog,
  RevokeEnrollmentCodeDialog,
} from "../../../features/agents/enrollment-dialogs.js";
import {
  useAgentPermissions,
  useAgents,
  useAgentsSummary,
  useEnrollmentCodes,
} from "../../../features/agents/queries.js";
import {
  AgentStationField,
  AgentStatusBadge,
  AgentsPage,
  agentsSearchShape,
  NoStations,
  OnlineBadge,
  RelativeTime,
  useAgentStations,
} from "../../../features/agents/shared.js";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../features/counterparties/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../lib/format.js";

const AGENT_TABS = ["agents", "codes"] as const;
type AgentTab = (typeof AGENT_TABS)[number];

const searchSchema = z.object({
  ...agentsSearchShape,
  tab: z.enum(AGENT_TABS).optional().catch(undefined),
  status: z.enum(AGENT_STATUSES).optional().catch(undefined),
  online: z.enum(["true", "false"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/ajanlar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: AgentsRoute,
});

/**
 * Ajanlar — istasyon ajanı modülünün açılış sayfası (Prompt 14).
 *
 * Üstte İSTASYON bazında özet (hangi istasyonda ajan var / çevrimiçi mi / kuyruk bekliyor mu), altta iki sekme:
 * ajan listesi ve kayıt kodları. Heartbeat her seferinde olay üretmediği için sorgular 30 sn'de bir tazelenir.
 */
function AgentsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <AgentsPage
      title={t("menu.agents")}
      description={t("agents.description")}
      breadcrumb={[{ label: t("menu.agents") }]}
    >
      {({ companyId }) => <AgentsBody companyId={companyId} />}
    </AgentsPage>
  );
}

function AgentsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useAgentPermissions();
  const station = useAgentStations(companyId);
  const tab: AgentTab = search.tab ?? "agents";

  const summary = useAgentsSummary(companyId);
  // Kayıt kodu penceresinin "bu istasyonda zaten etkin ajan var" uyarısı özetten beslenir (ek istek yok).
  const stationsWithActiveAgent = useMemo(
    () =>
      new Set(
        (summary.data?.stations ?? [])
          .filter((entry) => entry.activeAgents > 0)
          .map((entry) => entry.stationId),
      ),
    [summary.data],
  );

  /**
   * `GET /agents/summary` yalnızca AJANI OLAN istasyonları döndürür (sorgusu `agents` tablosundan başlar).
   * Pano ise "hangi istasyona ajan KURULMAMIŞ" sorusunu yanıtlamalı → şirketin istasyon listesiyle birleştirilir
   * ve ajanı olmayan istasyon sıfırlı satır olarak çizilir. (API BULGUSU: rapor.)
   */
  const summaryRows = useMemo<AgentStationSummary[]>(
    () =>
      station.stations.map((item) => {
        const found = (summary.data?.stations ?? []).find((entry) => entry.stationId === item.id);
        return (
          found ?? {
            stationId: item.id,
            stationCode: item.code,
            stationName: item.name,
            activeAgents: 0,
            onlineAgents: 0,
            offlineAgents: 0,
            oldestPendingAt: null,
            pendingCommands: 0,
            openAlarms: 0,
          }
        );
      }),
    [station.stations, summary.data],
  );

  const [creating, setCreating] = useState(false);
  /**
   * Pencerenin varsayılan istasyonu AYRI durumda tutulur: pano kısayolu hem süzgeci değiştirir hem pencereyi
   * açar, ama yönlendirici gezinmesi ASENKRONDUR → `station.stationId` pencere açılırken henüz eski değeri
   * taşıyabilir ve kullanıcı başka bir istasyonun kodunu üretirdi.
   */
  const [createStationId, setCreateStationId] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedAgentEnrollmentCode | null>(null);

  const stationLabel = (stationId: string): string => {
    const found = station.stations.find((item) => item.id === stationId);
    return found ? `${found.code} ${found.name}` : stationId;
  };

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  const openCreate = (stationId?: string) => {
    setCreateStationId(stationId ?? station.stationId);
    if (stationId) station.select(stationId);
    setCreating(true);
  };

  return (
    <>
      <StationSummary
        summary={summaryRows}
        isPending={summary.isPending}
        error={summary.error}
        canManage={permissions.canManage}
        onCreateCode={openCreate}
      />

      <Tabs
        className="grid gap-4"
        value={tab}
        onValueChange={(next) =>
          void navigate({
            to: ".",
            search: ((previous: Record<string, unknown>) => ({ ...previous, tab: next })) as never,
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("agents.tabsLabel")}>
          {AGENT_TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`agents.tab.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* İçerik MUTLAKA `TabsContent` içinde: tetikleyicinin `aria-controls` hedefi var olmalı. */}
        <TabsContent value={tab} className="grid gap-4 pt-0">
          {tab === "agents" ? <AgentsTab companyId={companyId} station={station} /> : null}
          {tab === "codes" ? (
            <CodesTab
              companyId={companyId}
              station={station}
              canManage={permissions.canManage}
              onCreate={() => openCreate()}
            />
          ) : null}
        </TabsContent>
      </Tabs>

      {permissions.canManage ? (
        <CreateEnrollmentCodeDialog
          open={creating}
          onOpenChange={setCreating}
          stations={station.stations}
          defaultStationId={createStationId}
          stationsWithActiveAgent={stationsWithActiveAgent}
          onCreated={setCreated}
        />
      ) : null}
      <RevealEnrollmentCodeDialog
        created={created}
        onClose={() => setCreated(null)}
        stationLabel={created ? stationLabel(created.stationId) : ""}
      />
    </>
  );
}

type StationSelection = ReturnType<typeof useAgentStations>;

/** İstasyon bazında pano: ajanı olmayan istasyon "Ajan kurulmamış" der ve kayıt kodu kısayolu sunar. */
function StationSummary({
  summary,
  isPending,
  error,
  canManage,
  onCreateCode,
}: {
  summary: readonly AgentStationSummary[];
  isPending: boolean;
  error: unknown;
  canManage: boolean;
  onCreateCode: (stationId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  if (error) return <Alert tone="danger">{errorMessage(error)}</Alert>;
  if (isPending) return <SkeletonRows rows={2} />;
  if (summary.length === 0) return <NoStations />;

  return (
    <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {summary.map((entry) => (
        <li key={entry.stationId}>
          <Card className="grid h-full gap-3 p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h2 className="text-sm font-semibold">
                <span className="font-mono">{entry.stationCode}</span> {entry.stationName}
              </h2>
              {entry.activeAgents === 0 ? (
                <StatusBadge tone="warning">{t("agents.summary.notInstalled")}</StatusBadge>
              ) : (
                <StatusBadge tone={entry.offlineAgents > 0 ? "warning" : "success"}>
                  {t("agents.summary.onlineOf", {
                    online: entry.onlineAgents,
                    total: entry.activeAgents,
                  })}
                </StatusBadge>
              )}
            </div>

            {entry.activeAgents === 0 ? (
              <>
                <p className="text-sm text-muted-foreground">{t("agents.summary.notInstalledHint")}</p>
                {canManage ? (
                  <div>
                    <Button variant="outline" size="sm" onClick={() => onCreateCode(entry.stationId)}>
                      <KeyRound aria-hidden="true" />
                      {t("agents.codes.createTitle")}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <dl className="grid grid-cols-2 gap-3">
                <div className="grid gap-0.5">
                  <dt className="text-xs text-muted-foreground">{t("agents.summary.offline")}</dt>
                  <dd className="text-sm font-medium tabular">{formatCount(entry.offlineAgents)}</dd>
                </div>
                <div className="grid gap-0.5">
                  <dt className="text-xs text-muted-foreground">{t("agents.summary.oldestPending")}</dt>
                  <dd className="text-sm font-medium">
                    {entry.oldestPendingAt === null ? (
                      t("agents.summary.queueEmpty")
                    ) : (
                      <RelativeTime value={entry.oldestPendingAt} />
                    )}
                  </dd>
                </div>
                <div className="grid gap-0.5">
                  <dt className="text-xs text-muted-foreground">{t("agents.fields.pendingCommands")}</dt>
                  <dd className="text-sm font-medium tabular">{formatCount(entry.pendingCommands)}</dd>
                </div>
                <div className="grid gap-0.5">
                  <dt className="text-xs text-muted-foreground">{t("agents.summary.openAlarms")}</dt>
                  <dd className="text-sm font-medium tabular">{formatCount(entry.openAlarms)}</dd>
                </div>
              </dl>
            )}
          </Card>
        </li>
      ))}
    </ul>
  );
}

// ── Ajanlar sekmesi ──────────────────────────────────────────────────────────
function AgentsTab({ companyId, station }: { companyId: string; station: StationSelection }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();

  const setFilters = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters = {
    stationId: station.stationId ?? undefined,
    status: search.status,
    online: search.online === undefined ? undefined : search.online === "true",
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useAgents(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<AgentSummary>[]>(
    () => [
      {
        id: "station",
        header: t("agents.fields.station"),
        hideable: false,
        exportValue: (row) => `${row.stationCode} ${row.stationName}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="font-mono">{row.stationCode}</span>
            <span className="text-xs text-muted-foreground">{row.stationName}</span>
          </span>
        ),
      },
      {
        id: "hostname",
        header: t("agents.fields.hostname"),
        hideable: false,
        exportValue: (row) => row.hostname,
        cell: (row) => (
          <Link to="/ajanlar/$id" params={{ id: row.id }} className="font-mono underline underline-offset-4">
            {row.hostname}
          </Link>
        ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => AGENT_STATUS_LABELS[row.status],
        cell: (row) => <AgentStatusBadge status={row.status} />,
      },
      {
        id: "online",
        header: t("agents.fields.online"),
        exportValue: (row) => (row.online ? t("agents.online") : t("agents.offline")),
        cell: (row) => <OnlineBadge online={row.online} />,
      },
      {
        id: "lastSeenAt",
        header: t("agents.fields.lastSeenAt"),
        exportValue: (row) => row.lastSeenAt ?? "",
        cell: (row) => <RelativeTime value={row.lastSeenAt} />,
      },
      {
        id: "version",
        header: t("agents.fields.version"),
        exportValue: (row) => row.version,
        cell: (row) => <span className="font-mono text-xs">{row.version}</span>,
      },
      {
        id: "queueDepth",
        header: t("agents.fields.queueDepth"),
        align: "right",
        exportValue: (row) => row.queueDepth ?? "",
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="tabular">{formatCount(row.queueDepth)}</span>
            {row.oldestPendingAt ? (
              <span className="text-xs text-muted-foreground">
                <RelativeTime value={row.oldestPendingAt} />
              </span>
            ) : null}
          </span>
        ),
      },
      {
        id: "clockSkew",
        header: t("agents.fields.clockSkew"),
        align: "right",
        exportValue: (row) => (row.clockSkewMs === null ? "" : String(row.clockSkewMs)),
        // Renk TEK BAŞINA anlam taşımaz: eşiği aşan sapmada METİNLİ uyarı rozeti de çizilir.
        cell: (row) => (
          <span className="grid justify-items-end gap-0.5">
            <span className="tabular">{formatClockSkew(row.clockSkewMs)}</span>
            {isClockSkewHigh(row.clockSkewMs) ? (
              <StatusBadge tone="warning">{t("agents.clockSkewHigh")}</StatusBadge>
            ) : null}
          </span>
        ),
      },
      {
        id: "adapters",
        header: t("agents.fields.adapters"),
        exportValue: (row) => `${row.adapterOk} / ${row.adapterTotal}`,
        // Sağlık özeti API'de son heartbeat'ten türetilir; rozet METİN taşır (yalnız renk değil).
        cell: (row) => (
          <StatusBadge
            tone={
              row.adapterTotal === 0 ? "neutral" : row.failingAdapters.length === 0 ? "success" : "danger"
            }
          >
            {row.adapterTotal === 0
              ? t("agents.adapters.none")
              : row.failingAdapters.length === 0
                ? t("agents.adapters.allOk", { ok: row.adapterOk, total: row.adapterTotal })
                : t("agents.adapters.failing", { names: row.failingAdapters.join(", ") })}
          </StatusBadge>
        ),
      },
      {
        id: "pendingCommands",
        header: t("agents.fields.pendingCommands"),
        align: "right",
        exportValue: (row) => String(row.pendingCommands),
        cell: (row) => <span className="tabular">{formatCount(row.pendingCommands)}</span>,
      },
    ],
    [t],
  );

  return (
    <DataTable
      caption={t("agents.caption")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<Server />}
          title={t("agents.emptyTitle")}
          description={t("agents.emptyDescription")}
        />
      }
      filters={
        <>
          <AgentStationField selection={station} />
          <FormField label={t("common.status")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.status ?? ""}
                onChange={(event) => setFilters({ status: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {AGENT_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {AGENT_STATUS_LABELS[value as AgentStatus]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("agents.fields.online")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.online ?? ""}
                onChange={(event) => setFilters({ online: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                <option value="true">{t("agents.online")}</option>
                <option value="false">{t("agents.offline")}</option>
              </Select>
            )}
          </FormField>
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}

// ── Kayıt kodları sekmesi ────────────────────────────────────────────────────
function CodesTab({
  companyId,
  station,
  canManage,
  onCreate,
}: {
  companyId: string;
  station: StationSelection;
  canManage: boolean;
  onCreate: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const codes = useEnrollmentCodes(companyId, station.stationId, true);
  const [revoking, setRevoking] = useState<AgentEnrollmentCode | null>(null);
  const items = codes.data ?? [];

  return (
    <>
      <Card className="flex flex-wrap items-end justify-between gap-3 p-4">
        <AgentStationField selection={station} />
        {canManage ? (
          <Button onClick={onCreate}>
            <Plus aria-hidden="true" />
            {t("agents.codes.createTitle")}
          </Button>
        ) : null}
      </Card>

      {codes.error ? (
        <Alert tone="danger">{errorMessage(codes.error)}</Alert>
      ) : codes.isPending ? (
        <SkeletonRows rows={4} />
      ) : items.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<KeyRound />}
          title={t("agents.codes.emptyTitle")}
          description={t("agents.codes.emptyDescription")}
        />
      ) : (
        <SimpleTable
          caption={t("agents.codes.caption")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("agents.fields.station")}
              </th>
              <th scope="col" className={thClass}>
                {t("agents.codes.note")}
              </th>
              <th scope="col" className={thClass}>
                {t("agents.codes.createdAt")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("agents.codes.expiresAt")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.status")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.actions")}
              </th>
            </tr>
          }
        >
          {items.map((code) => {
            const state = enrollmentCodeState(code);
            return (
              <tr key={code.id} className="border-t align-top">
                <td className={tdClass}>
                  <span className="font-mono">{code.stationCode}</span> {code.stationName}
                </td>
                <td className={tdClass}>{code.note ?? "—"}</td>
                <td className={`${tdClass} tabular`}>{formatDateTime(code.createdAt)}</td>
                <td className={tdRightClass}>{formatDateTime(code.expiresAt)}</td>
                <td className={tdClass}>
                  <StatusBadge tone={ENROLLMENT_CODE_STATE_TONE[state]}>
                    {t(`agents.codes.state.${state}`)}
                  </StatusBadge>
                </td>
                <td className={tdClass}>
                  {canManage && state === "ACTIVE" ? (
                    <Button variant="outline" size="sm" onClick={() => setRevoking(code)}>
                      {t("agents.codes.revokeAction")}
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </SimpleTable>
      )}

      <RevokeEnrollmentCodeDialog code={revoking} onClose={() => setRevoking(null)} />
    </>
  );
}
